import React from 'react';
import type { BlockerFunction, Location } from 'react-router';
import { createPath, NavigationType, useBlocker } from 'react-router';

import type { MayBe } from 'types/common';

type BrowserHistoryState = MayBe<{ key?: string }>;

const isBrowserOnEntry = (location: Location) => {
  const { pathname, search, hash } = window.location;
  // Same fallback as react-router's browser history: the initial entry has no key in its state.
  const browserEntryKey = (window.history.state as BrowserHistoryState)?.key || 'default';

  return browserEntryKey === location.key && `${pathname}${search}${hash}` === createPath(location);
};

/**
 * On a blocked POP, react-router puts the browser back on the current entry with an asynchronous `history.go()`.
 * Until that lands, the browser still sits on the blocked entry, whose history state holds its location key.
 * Navigating before that (e.g. `logout` pushing `/`) would push onto the wrong entry, so wait for the `popstate`.
 * That `popstate` always follows the `history.go()`. The listener is deliberately not removed on unmount: the user
 * already confirmed, so the callback must still run.
 */
const runAfterBrowserRestored = (blockedLocation: Location, fn: () => void) => {
  if (!isBrowserOnEntry(blockedLocation)) {
    fn();
    return;
  }

  window.addEventListener('popstate', () => fn(), { once: true });
};

/**
 * Prompts before a browser back/forward navigation (POP) when `shouldBlock` and `shouldPrompt(nextLocation)` are true.
 * The navigation is always cancelled once blocked; `callback` runs when the user confirms the prompt.
 * While `shouldBlock` is true, the browser also asks for confirmation before a reload / tab close.
 */
function useRouterPrompt(
  message: string,
  shouldBlock: boolean,
  callback: () => void,
  shouldPrompt: (nextLocation: Location) => boolean,
) {
  const shouldBlockNavigation = React.useCallback<BlockerFunction>(
    ({ historyAction, nextLocation }) => shouldBlock && historyAction === NavigationType.Pop && shouldPrompt(nextLocation),
    [shouldBlock, shouldPrompt],
  );

  const blocker = useBlocker(shouldBlockNavigation);

  // Same as `history.block` used to do: ask the browser to confirm a reload / tab close while blocking.
  React.useEffect(() => {
    if (!shouldBlock) {
      return;
    }

    const promptBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', promptBeforeUnload);

    return () => window.removeEventListener('beforeunload', promptBeforeUnload);
  }, [shouldBlock]);

  React.useEffect(() => {
    if (blocker.state !== 'blocked') {
      return;
    }

    const confirmed = window.confirm(message);
    const blockedLocation = blocker.location;
    blocker.reset();
    if (confirmed) {
      runAfterBrowserRestored(blockedLocation, callback);
    }
  }, [blocker, message, callback]);
}

export default useRouterPrompt;
